import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { AppRoute } from '../../../types/routing';
import NotFound from '../../../root/presentation/pages/NotFoundPage';
import Login from '../../auth/presentation/pages/LoginPage';
import WorkspaceRedirect from './components/WorkspaceRedirect';
import { lazyRoute } from './lazyRoute';

// Every page below is fetched on demand. Before this, all 153 of them were
// static imports, so opening the login screen downloaded the grant editor,
// the workflow canvas, the PDF viewer and every chart in the product — one
// 10 MB JavaScript file, uncompressed over the wire, before anything drew.
//
// `lazyRoute` is `React.lazy` plus a retry, because on the connections this
// app is used on a chunk request can stall rather than fail. The Suspense
// boundary that catches these lives in `root/presentation/App.tsx`, above
// every route, so each one resolves into the same waiting state.
//
// Two routes stay eager on purpose:
//   * `Login`   — where an unauthenticated first visit lands. Lazy would add
//                 a second serial round trip to the one visit this change
//                 exists to make fast.
//   * `NotFound` — the fallback for an unmatched path. It must be able to
//                 render when the network is the thing that is broken.

// The section gate renders the workspace shell (`Partials/Layout` — header,
// sidebar, right rail and the charts they carry) for its restricted and
// loading views. Statically imported it put that entire shell, and the
// chart and animation libraries hanging off it, in front of the login
// screen. Lazy, it lands in the chunk the workspace pages pull anyway.
const WorkspaceSectionGate = lazyRoute(
  () => import('../../workspace/presentation/WorkspaceSectionGate')
);
// `/settings` needs a scope-aware gate rather than `withSection('settings')`:
// the route serves the viewer's own account AND the workspace's configuration,
// told apart by `?scope=`. See SettingsSectionGate for why.
const SettingsSectionGate = lazyRoute(
  () => import('../../settings/presentation/SettingsSectionGate')
);

const Home = lazyRoute(() => import('../../home/presentation/pages/HomePage'));
const HomeRedirect = lazyRoute(
  () => import('../../home/presentation/pages/HomeRedirectPage')
);
const Dashboard = lazyRoute(
  () => import('../../home/presentation/pages/DashboardPage')
);
const NotificationsPage = lazyRoute(
  () => import('../../notifications/presentation/pages/NotificationsPage')
);
const MyWalletPage = lazyRoute(
  () => import('../../payments/presentation/pages/MyWalletPage')
);
const Welcome = lazyRoute(
  () => import('../../home/presentation/pages/WelcomePage')
);
const Register = lazyRoute(
  () => import('../../auth/presentation/pages/RegisterPage')
);
const InviteAcceptPage = lazyRoute(
  () => import('../../auth/presentation/pages/InviteAcceptPage')
);
const JoinPage = lazyRoute(
  () => import('../../auth/presentation/pages/JoinPage')
);
const Shop = lazyRoute(() => import('../../shop/presentation/pages/ShopPage'));
const Cart = lazyRoute(() => import('../../shop/presentation/pages/CartPage'));
const Store = lazyRoute(
  () => import('../../shop/presentation/pages/StorePage')
);
const Filter = lazyRoute(
  () => import('../../shop/presentation/pages/FiltersPage')
);
const ProductListPage = lazyRoute(
  () => import('../../shop/presentation/pages/ProductListPage')
);
const ProductImagesPage = lazyRoute(
  () => import('../../shop/presentation/pages/ProductImagesPage')
);
const ProductDetailPage = lazyRoute(
  () => import('../../shop/presentation/pages/ProductDetailPage')
);
const ProductsCatalogPage = lazyRoute(
  () => import('../../shop/presentation/pages/ProductsCatalogPage')
);
const Sunrise = lazyRoute(
  () => import('../../marketing/presentation/pages/SunrisePage')
);
const About = lazyRoute(
  () => import('../../marketing/presentation/pages/AboutPage')
);
const ConfirmEmail = lazyRoute(
  () => import('../../auth/presentation/pages/ConfirmEmailPage')
);
const EmailConfirmed = lazyRoute(
  () => import('../../auth/presentation/pages/EmailConfirmedPage')
);
const AcceptInvite = lazyRoute(
  () => import('../../auth/presentation/pages/AcceptInvitePage')
);
const UserProfile = lazyRoute(
  () => import('../../user/profile/presentation/pages/PublicUserProfilePage')
);
const AuthProfilePage = lazyRoute(
  () => import('../../user/profile/presentation/pages/AuthProfilePage')
);
const OrganizationProfilePage = lazyRoute(
  () => import('../../user/profile/presentation/pages/OrganizationProfilePage')
);
const UserProfilePage = lazyRoute(
  () => import('../../user/profile/presentation/pages/UserProfilePage')
);
const PodProfile = lazyRoute(
  () => import('../../seed/presentation/pages/PodProfilePage')
);
const SeedProfile = lazyRoute(
  () => import('../../seed/presentation/pages/SeedProfilePage')
);
const Contact = lazyRoute(
  () => import('../../marketing/presentation/pages/ContactPage')
);
const FileUploader = lazyRoute(
  () => import('../../uploads/presentation/pages/FileUploaderPage')
);
const Conversations = lazyRoute(
  () => import('../../user/messages/presentation/pages/ConversationsPage')
);
const Sprout = lazyRoute(
  () => import('../../ai-chat/presentation/pages/SproutPage')
);
const TeamDashboard = lazyRoute(
  () => import('../../team/presentation/pages/TeamDashboard')
);
const TeamEvents = lazyRoute(
  () => import('../../team/presentation/pages/TeamEvents')
);
const DirectoriesWorkflow = lazyRoute(
  () => import('../../team/presentation/pages/DirectoriesWorkflow')
);
const ContactsPage = lazyRoute(
  () => import('../../contacts/presentation/pages/ContactsPage')
);
const Projects = lazyRoute(
  () => import('../../projects/presentation/pages/ProjectsPage')
);
const SponsorProjectsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorProjectsPage')
);
const WorkspaceProjectsPage = lazyRoute(
  () => import('../../projects/presentation/pages/WorkspaceProjectsPage')
);
const TeamsIndexPage = lazyRoute(
  () => import('../../team/presentation/pages/TeamsIndexPage')
);
const MyWorkPage = lazyRoute(
  () => import('../../team/presentation/pages/MyWorkPage')
);
const MySpendingPage = lazyRoute(
  () => import('../../reports/presentation/pages/MySpendingPage')
);
const SponsorCampaignsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorCampaignsPage')
);
const SponsorEventsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorEventsPage')
);
const SponsorRecipientsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorRecipientsPage')
);
const SponsorTransparencyPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorTransparencyPage')
);
const SponsorMySponsorshipsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorMySponsorshipsPage')
);
const SponsorReceiptsPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorReceiptsPage')
);
const SponsorUpdatesPage = lazyRoute(
  () => import('../../sponsor/presentation/pages/SponsorUpdatesPage')
);
const Income = lazyRoute(
  () => import('../../transactions/presentation/pages/IncomePage')
);
const Expenses = lazyRoute(
  () => import('../../transactions/presentation/pages/ExpensesPage')
);
const RecycleBinPage = lazyRoute(
  () => import('../../recycleBin/presentation/pages/RecycleBinPage')
);
const CategoriesPage = lazyRoute(
  () => import('../../budget/presentation/pages/CategoriesPage')
);
const Transactions = lazyRoute(
  () => import('../../transactions/presentation/pages/TransactionsPage')
);
const Settings = lazyRoute(
  () => import('../../settings/presentation/pages/SettingsPage')
);
const HelpPage = lazyRoute(
  () => import('../../help/presentation/pages/HelpPage')
);
const Assignments = lazyRoute(
  () => import('../../team/presentation/pages/Assignments')
);
const AiAgentsPage = lazyRoute(
  () => import('../../agents/presentation/pages/AiAgentsPage')
);
const AiDashboardPage = lazyRoute(
  () => import('../../agents/presentation/pages/AiDashboardPage')
);
const CommandCenterV2Page = lazyRoute(
  () => import('../../agents/presentation/pages/CommandCenterV2Page')
);
const AIQualityAnalyticsPage = lazyRoute(
  () => import('../../agents/presentation/pages/AIQualityAnalyticsPage')
);
const AIActionsRedirect = lazyRoute(
  () => import('../../ai-actions/presentation/components/AIActionsRedirect')
);
const Reset = lazyRoute(
  () => import('../../auth/presentation/pages/ResetPage')
);
const PasswordResetConfirm = lazyRoute(
  () => import('../../auth/presentation/pages/PasswordResetConfirmPage')
);
const ResetConfirm = lazyRoute(
  () => import('../../auth/presentation/pages/ResetConfirmPage')
);
const ResetPasswordSuccess = lazyRoute(
  () => import('../../auth/presentation/pages/ResetPasswordSuccessPage')
);
const RegistrationError = lazyRoute(
  () => import('../../auth/presentation/pages/RegistrationErrorPage')
);
const PlantSeed = lazyRoute(
  () => import('../../seed/presentation/pages/PlantSeedPage')
);
const Onboard = lazyRoute(
  () => import('../../seed/presentation/pages/OnboardPage')
);
const SeedOperation = lazyRoute(
  () => import('../../seed/presentation/pages/SeedOperationPage')
);
const BudgetDashboard = lazyRoute(
  () => import('../../budget/presentation/pages/BudgetDashboardRedirectPage')
);
const BudgetDashboardV2 = lazyRoute(
  () => import('../../budget/presentation/pages/BudgetDashboardHistoryPage')
);
const BankConnectionsPage = lazyRoute(
  () => import('../../budget/presentation/pages/BankConnectionsPage')
);
const BankFeedInboxPage = lazyRoute(
  () => import('../../budget/presentation/pages/BankFeedInboxPage')
);
const BankSectionPage = lazyRoute(
  () => import('../../budget/presentation/pages/BankSectionPage')
);
const TwoTierPricing = lazyRoute(
  () => import('../../subscription/presentation/pages/TwoTierPricingPage')
);
const Sponsorship = lazyRoute(
  () => import('../../sponsorship/presentation/pages/SponsorshipPage')
);
const UpdatesDuePage = lazyRoute(
  () => import('../../sponsorship/presentation/pages/UpdatesDuePage')
);
const SponsorChild = lazyRoute(
  () => import('../../sponsorship/presentation/pages/SponsorPage')
);
const CampaignsPage = lazyRoute(
  () => import('../../campaigns/presentation/pages/CampaignsPage')
);
const SocialMediaCampaigns = lazyRoute(
  () => import('../../campaigns/presentation/pages/SocialMediaCampaignsPage')
);
const NewsletterCampaigns = lazyRoute(
  () => import('../../campaigns/presentation/pages/NewsletterCampaignsPage')
);
const AiDocumentsPage = lazyRoute(
  () => import('../../documents/presentation/pages/AiDocumentsPage')
);
const WritingLibraryPage = lazyRoute(
  () => import('../../writing/presentation/pages/WritingLibraryPage')
);
const LegalDocumentsPage = lazyRoute(
  () => import('../../legal/presentation/pages/LegalDocumentsPage')
);
const DraftsListPage = lazyRoute(
  () => import('../../writing/presentation/pages/DraftsListPage')
);
const NewslettersListPage = lazyRoute(
  () => import('../../writing/presentation/pages/NewslettersListPage')
);
const BlogsListPage = lazyRoute(
  () => import('../../writing/presentation/pages/BlogsListPage')
);
const SubscribersPage = lazyRoute(
  () => import('../../writing/presentation/pages/SubscribersPage')
);
const DraftEditorPage = lazyRoute(
  () => import('../../writing/presentation/pages/DraftEditorPage')
);
const NewsletterEditorPage = lazyRoute(
  () => import('../../writing/presentation/pages/NewsletterEditorPage')
);
const BlogEditorPage = lazyRoute(
  () => import('../../writing/presentation/pages/BlogEditorPage')
);
const UnsubscribeLandingPage = lazyRoute(
  () => import('../../writing/presentation/pages/UnsubscribeLandingPage')
);
const ConfirmSubscriptionPage = lazyRoute(
  () => import('../../writing/presentation/pages/ConfirmSubscriptionPage')
);
const SignOffInboxPage = lazyRoute(
  () => import('../../signOff/presentation/pages/SignOffInboxPage')
);
const IntegrationsPage = lazyRoute(
  () => import('../../integrations/presentation/pages/IntegrationsPage')
);
const WorkspaceFeedPage = lazyRoute(
  () => import('../../workspaceFeed/presentation/pages/WorkspaceFeedPage')
);
const MarketPlacePage = lazyRoute(
  () => import('../../shop/presentation/pages/MarketplacePage')
);
const ShopDetailsPage = lazyRoute(
  () => import('../../shop/presentation/pages/MarketplaceShopDetailsPage')
);
const UploadProductPage = lazyRoute(
  () => import('../../shop/presentation/pages/MarketplaceUploadProductPage')
);
const ShopCheckoutPage = lazyRoute(
  () => import('../../shop/presentation/pages/ShopCheckoutPage')
);
const ShopCheckoutSuccessPage = lazyRoute(
  () => import('../../shop/presentation/pages/ShopCheckoutSuccessPage')
);
const SearchResults = lazyRoute(
  () => import('../../search/presentation/pages/SearchResultsPage')
);
const Donations = lazyRoute(
  () => import('../../donations/presentation/pages/DonationsPage')
);
const GrantsPipelinePage = lazyRoute(
  () => import('../../grants/presentation/pages/GrantsPipelinePage')
);
const PayStatementsPage = lazyRoute(
  () => import('../../payroll/presentation/pages/PayStatementsPage')
);
const PublicBookingPage = lazyRoute(
  () => import('../../scheduling/presentation/pages/PublicBookingPage')
);
const BookingManagePage = lazyRoute(
  () => import('../../scheduling/presentation/pages/BookingManagePage')
);
const SchedulingPage = lazyRoute(
  () => import('../../scheduling/presentation/pages/SchedulingPage')
);
const GrantDetailPage = lazyRoute(
  () => import('../../grants/presentation/pages/GrantDetailPage')
);
const GrantApplicationEditor = lazyRoute(
  () => import('../../grants/presentation/pages/GrantApplicationEditor')
);
const GrantApplicationsPage = lazyRoute(
  () => import('../../grants/presentation/pages/GrantApplicationsPage')
);
const ReportEditor = lazyRoute(
  () => import('../../grants/presentation/pages/ReportEditor')
);
const PublicGrantPage = lazyRoute(
  () => import('../../grants/presentation/pages/PublicGrantPage')
);
const PublicWorkspaceGrantsPage = lazyRoute(
  () => import('../../grants/presentation/pages/PublicWorkspaceGrantsPage')
);
const ComplianceStatementsPage = lazyRoute(
  () => import('../../grants/presentation/pages/ComplianceStatementsPage')
);
const FunderDirectoryPage = lazyRoute(
  () => import('../../grants/presentation/pages/FunderDirectoryPage')
);
const FunderCatalogPage = lazyRoute(
  () => import('../../grants/presentation/pages/FunderCatalogPage')
);
const SnippetLibraryPage = lazyRoute(
  () => import('../../grants/presentation/pages/SnippetLibraryPage')
);
const OpportunitiesPage = lazyRoute(
  () => import('../../grants/presentation/pages/OpportunitiesPage')
);
const GrantMatchesPage = lazyRoute(
  () => import('../../grants/presentation/pages/GrantMatchesPage')
);
const ReportingCalendarPage = lazyRoute(
  () => import('../../grants/presentation/pages/ReportingCalendarPage')
);
const FinancialReports = lazyRoute(
  () => import('../../reports/presentation/pages/FinancialReportsPage')
);
const AuditTrailPage = lazyRoute(
  () => import('../../auditor/presentation/pages/AuditTrailPage')
);
const AuditSampleTestingPage = lazyRoute(
  () => import('../../auditor/presentation/pages/AuditSampleTestingPage')
);
const ContributorOnboard = lazyRoute(
  () => import('../../onboarding/presentation/pages/ContributorOnboardPage')
);
const AllTemplatesPage = lazyRoute(
  () => import('../../templates/presentation/pages/AllTemplatesPage')
);
const DonationFormsListPage = lazyRoute(
  () => import('../../donation-forms/presentation/pages/DonationFormsListPage')
);
const PublicDonationFormPage = lazyRoute(
  () => import('../../donation-forms/presentation/pages/PublicDonationFormPage')
);
const ReliefAppealsListPage = lazyRoute(
  () => import('../../relief/presentation/pages/ReliefAppealsListPage')
);
const PublicReliefAppealPage = lazyRoute(
  () => import('../../relief/presentation/pages/PublicReliefAppealPage')
);
const PublicCampaignDonatePage = lazyRoute(
  () =>
    import('../../public-donate/presentation/pages/PublicCampaignDonatePage')
);
const PublicWorkspaceRecipientsPage = lazyRoute(
  () =>
    import(
      '../../public-donate/presentation/pages/PublicWorkspaceRecipientsPage'
    )
);
const PublicProductBuyPage = lazyRoute(
  () => import('../../public-donate/presentation/pages/PublicProductBuyPage')
);
const MyDonationsPage = lazyRoute(
  () => import('../../public-donate/presentation/pages/MyDonationsPage')
);
const MagicLinkRequestPage = lazyRoute(
  () => import('../../public-donate/presentation/pages/MagicLinkRequestPage')
);
const MagicLinkVerifyPage = lazyRoute(
  () => import('../../public-donate/presentation/pages/MagicLinkVerifyPage')
);
const ShareAcceptPage = lazyRoute(
  () => import('../../sharing/presentation/pages/ShareAcceptPage')
);
const ShareInvitationAcceptPage = lazyRoute(
  () => import('../../sharing/presentation/pages/ShareInvitationAcceptPage')
);
const ShareBudgetViewerPage = lazyRoute(
  () => import('../../sharing/presentation/pages/ShareBudgetViewerPage')
);
const ShareReportViewerPage = lazyRoute(
  () => import('../../sharing/presentation/pages/ShareReportViewerPage')
);
const WorkspaceSharesPage = lazyRoute(
  () => import('../../sharing/presentation/pages/WorkspaceSharesPage')
);
const ActiveBids = lazyRoute(() => import('components/ActiveBids'));
const ThemeMarketPlace = lazyRoute(() => import('components/MarketPlace'));
const ThemeMyCollection = lazyRoute(() => import('components/MyCollection'));
const ThemeSaved = lazyRoute(() => import('components/Saved'));
const ThemeSell = lazyRoute(() => import('components/Sell'));
const ThemeThankYou = lazyRoute(() => import('components/AuthPages/ThankYou'));

const ProfileRedirect = () => <Navigate to="/user-profile" replace />;

// Redirect to a fixed path while carrying the incoming query string forward.
// Used by the legacy /login → /identity/login redirect so a pre-branded demo
// link like /login?ws=<uuid> keeps its ?ws param and the login screen can
// still resolve the workspace brand after the redirect.
const RedirectPreservingQuery = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

// The retired /writing/compose page → the drafts list, which auto-opens the
// reusable ComposeModal. Carries kind/template_id from old links forward and
// adds compose=1 as the open signal.
const ComposeRedirect = () => {
  const { workspaceId } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('compose', '1');
  return (
    <Navigate
      to={`/w/${workspaceId}/writing/drafts?${params.toString()}`}
      replace
    />
  );
};

// Helper: wraps a page component with WorkspaceSectionGate so users
// without the section's visible_sections see a Restricted view instead
// of the page. Closes the URL-typing leak (sidebar hides the menu
// entry, but typing the URL straight in used to render the page). Also
// closes the impersonation-end window where the page remounts (via
// WorkspaceLayout's persona-keyed subtree) — without this gate the
// remount would render the page again with the now-restricted
// permissions.
type GateSection =
  | 'finance'
  | 'teams'
  | 'work'
  | 'contacts'
  | 'giving'
  | 'transparency'
  | 'workflows'
  | 'beneficiaries'
  | 'settings'
  | 'admin'
  | 'ai';

// `/settings` only. Wraps the page in the scope-aware gate so the workspace
// scope is checked against the `settings` ceiling while the personal scope
// passes through — see SettingsSectionGate.
const withSettingsScope = (Component: React.ComponentType) => {
  const Wrapped: React.FC = () => (
    <SettingsSectionGate>
      <Component />
    </SettingsSectionGate>
  );
  Wrapped.displayName = `GatedSettings(${
    Component.displayName || Component.name || 'Component'
  })`;
  return Wrapped;
};

const withSection = (section: GateSection, Component: React.ComponentType) => {
  const Wrapped: React.FC = () => (
    <WorkspaceSectionGate section={section}>
      <Component />
    </WorkspaceSectionGate>
  );
  Wrapped.displayName = `Gated(${section},${
    Component.displayName || Component.name || 'Component'
  })`;
  return Wrapped;
};

// Canonical `/w/:workspaceId/<resource>` paths redirect to the legacy
// `/<resource>/:workspaceId` routes. This makes canonical URLs work for
// deep links + shares without requiring every workspace-scoped page
// component to migrate off `useParams().id` / `.seed` / `.seed_id` in a
// single pass — the migration to useActiveWorkspace happens
// incrementally (foundation plan B7 follow-up). Once all components read
// the workspace via useActiveWorkspace, we flip the direction so legacy
// paths redirect to canonical.
const buildCanonicalRedirect = (build: (workspaceId: string) => string) => {
  const RedirectComponent = () => {
    const { workspaceId } = useParams();
    const location = useLocation();
    if (!workspaceId) return <Navigate to="/dashboard" replace />;
    return <Navigate to={`${build(workspaceId)}${location.search}`} replace />;
  };
  return RedirectComponent;
};

const CanonicalDashboardRedirect = buildCanonicalRedirect(
  (id) => `/dashboard/${id}`
);
const CanonicalHomeRedirect = buildCanonicalRedirect((id) => `/home/${id}`);
const CanonicalSponsorshipRedirect = buildCanonicalRedirect(
  (id) => `/sponsorship/${id}`
);
const CanonicalCampaignsRedirect = buildCanonicalRedirect(
  (id) => `/campaigns/${id}`
);
const CanonicalDonationsRedirect = buildCanonicalRedirect(
  (id) => `/donations/${id}`
);
// Templates consolidation: the bespoke writing + budget template pages are
// retired in favour of the unified Templates hub (/templates/:seed). Keep the
// old paths as redirects (bookmarks, deep links) rather than 404ing them.
const WritingTemplatesRedirect = buildCanonicalRedirect(
  (id) => `/templates/${id}`
);
const BudgetTemplatesRedirect: React.FC = () => {
  const { seed } = useParams();
  const location = useLocation();
  if (!seed) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/templates/${seed}${location.search}`} replace />;
};
const CanonicalTransparencyRedirect = buildCanonicalRedirect(
  (id) => `/transparency/${id}`
);
const CanonicalMySponsorshipsRedirect = buildCanonicalRedirect(
  (id) => `/sponsorship/mine/${id}`
);
const CanonicalMyReceiptsRedirect = buildCanonicalRedirect(
  (id) => `/receipts/mine/${id}`
);
const CanonicalMyUpdatesRedirect = buildCanonicalRedirect(
  (id) => `/updates/mine/${id}`
);
const CanonicalGrantsRedirect = buildCanonicalRedirect((id) => `/grants/${id}`);
const CanonicalGrantsFundersRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/funders`
);
const CanonicalGrantsOpportunitiesRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/opportunities`
);
const CanonicalGrantsSnippetsRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/snippets`
);
const CanonicalGrantsApplicationsRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/applications`
);
// Discover grants merged into Grant matches ("All grants" scope) — the old
// canonical /w/.../grants/discover path now lands on the consolidated matches
// surface so existing links/bookmarks keep working.
const CanonicalGrantsDiscoverRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/matches`
);
const GrantsDiscoverRedirect: React.FC = () => {
  const { seed } = useParams();
  const location = useLocation();
  if (!seed) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/grants/${seed}/matches${location.search}`} replace />;
};
const CanonicalGrantsReportingRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/reporting`
);
const CanonicalGrantsComplianceRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/compliance`
);
const CanonicalGrantsDirectoryRedirect = buildCanonicalRedirect(
  (id) => `/grants/${id}/directory`
);
const CanonicalMessagesRedirect = buildCanonicalRedirect(
  (id) => `/messages/${id}`
);
const CanonicalAiDashboardRedirect = buildCanonicalRedirect(
  (id) => `/ai/dashboard/${id}`
);
const CanonicalAiV2Redirect = buildCanonicalRedirect((id) => `/ai/v2/${id}`);
const CanonicalAiAgentsRedirect = buildCanonicalRedirect(
  (id) => `/ai/agents/${id}`
);
const CanonicalBudgetDashboardRedirect = buildCanonicalRedirect(
  (id) => `/budget/dashboard-v2/${id}`
);
const CanonicalFundraisingEventsRedirect = buildCanonicalRedirect(
  (id) => `/fundraising/events/${id}`
);
const CanonicalTransactionsIncomeRedirect = buildCanonicalRedirect(
  (id) => `/transactions/income/${id}`
);
const CanonicalTransactionsExpensesRedirect = buildCanonicalRedirect(
  (id) => `/transactions/expenses/${id}`
);
const CanonicalTransactionsAllRedirect = buildCanonicalRedirect(
  (id) => `/transactions/all/${id}`
);
const CanonicalFinanceReportsRedirect = buildCanonicalRedirect(
  (id) => `/finance/reports/${id}`
);
const CanonicalTeamsDocumentsRedirect = buildCanonicalRedirect(
  (id) => `/teams/documents/${id}`
);
const CanonicalAiDocumentsRedirect = buildCanonicalRedirect(
  (id) => `/ai/documents/${id}`
);
const CanonicalFeedRedirect = buildCanonicalRedirect(
  (id) => `/workspaces/${id}/feed`
);
// Contacts resolves its workspace from the ?seedId query (DirectoriesLanding),
// so the canonical /w/:id/contacts alias forwards the workspace id as ?seedId
// rather than a path segment. Custom (not buildCanonicalRedirect) to put the
// id in the query, mirroring the sidebar link.
const CanonicalContactsRedirect = () => {
  const { workspaceId } = useParams();
  if (!workspaceId) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/contacts?seedId=${workspaceId}`} replace />;
};

// Sign-off inbox renders at the canonical /w/:workspaceId/sign-off (like the
// writing surface). Keep a short legacy /sign-off/:seed alias so bookmarks /
// deep links forward to the canonical path.
const SignOffLegacyRedirect = () => {
  const { seed, seed_id: seedId } = useParams();
  const id = seed ?? seedId;
  const location = useLocation();
  if (!id) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/w/${id}/sign-off${location.search}`} replace />;
};

// The legacy /teams/directories page (Contacts + Invitations + Groups +
// Permissions tabs) was retired in the nav rework: Contacts moved to /contacts,
// member admin to Settings ▸ Members & Access. Forward any remaining deep links
// (bookmarks, the old personal "People" link) to /contacts, preserving the
// ?seedId query. The /teams/directories/workflows builder is a separate route
// and is unaffected.
const DirectoriesRetiredRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/contacts${location.search}`} replace />;
};

// Legacy reset emails link to /PasswordResetConfirm/<uid>/<token>/. Carry
// the params + query (redirect_url) through to the canonical route —
// dropping them lands on a param-less path that 404s.
const PasswordResetLegacyRedirect = () => {
  const { uidb64, token } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/identity/password-reset-confirm/${uidb64}/${token}${location.search}`}
      replace
    />
  );
};
const DashboardRedirect = () => (
  <WorkspaceRedirect buildPath={(workspaceId) => `/dashboard/${workspaceId}`} />
);
const MessagesRedirect = () => (
  <WorkspaceRedirect buildPath={(workspaceId) => `/messages/${workspaceId}`} />
);
const SponsorshipRedirect = () => (
  <WorkspaceRedirect
    buildPath={(workspaceId) => `/sponsorship/${workspaceId}`}
  />
);
const CampaignsRedirect = () => (
  <WorkspaceRedirect buildPath={(workspaceId) => `/campaigns/${workspaceId}`} />
);

// Forward `/budget/bank-connections/:seed` and `/budget/bank-feed-inbox/:seed`
// to the canonical `/budget/bank/:seed?tab=…` shell so bookmarks keep
// working. The shell owns the IA — only one URL per workspace.
const BankConnectionsLegacyRedirect = () => {
  const { seed, seed_id: seedId } = useParams();
  const id = seed ?? seedId;
  const location = useLocation();
  if (!id) return <Navigate to="/dashboard" replace />;
  const search = location.search
    ? `${location.search}&tab=connections`
    : '?tab=connections';
  return <Navigate to={`/budget/bank/${id}${search}`} replace />;
};
const BankFeedInboxLegacyRedirect = () => {
  const { seed, seed_id: seedId } = useParams();
  const id = seed ?? seedId;
  const location = useLocation();
  if (!id) return <Navigate to="/dashboard" replace />;
  const search = location.search
    ? `${location.search}&tab=inbox`
    : '?tab=inbox';
  return <Navigate to={`/budget/bank/${id}${search}`} replace />;
};
const DonationsRedirect = () => (
  <WorkspaceRedirect buildPath={(workspaceId) => `/donations/${workspaceId}`} />
);
const GrantsRedirect = () => (
  <WorkspaceRedirect buildPath={(workspaceId) => `/grants/${workspaceId}`} />
);
const FundraisingEventsRedirect = () => (
  <WorkspaceRedirect
    buildPath={(workspaceId) => `/fundraising/events/${workspaceId}`}
  />
);
const DonationFormsRedirect = () => (
  <WorkspaceRedirect
    buildPath={(workspaceId) => `/fundraising/forms/${workspaceId}`}
  />
);
const ReliefRedirect = () => (
  <WorkspaceRedirect
    buildPath={(workspaceId) => `/fundraising/relief/${workspaceId}`}
  />
);

const routes: AppRoute[] = [
  // ── Canonical /w/:workspaceId/* aliases ──────────────────────────
  // Each redirects to its legacy `/<resource>/:workspaceId` route, so
  // deep links / shares using the canonical pattern keep working
  // without rewriting every page component. Component-level migration
  // off `useParams().id|seed|seed_id` to `useActiveWorkspace()` is the
  // follow-up that lets us flip the redirect direction.
  {
    path: '/w/:workspaceId/dashboard',
    element: CanonicalDashboardRedirect,
    title: 'Dashboard',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/home',
    element: CanonicalHomeRedirect,
    title: 'Home',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/sponsorship',
    element: CanonicalSponsorshipRedirect,
    title: 'Sponsorship',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/campaigns',
    element: CanonicalCampaignsRedirect,
    title: 'Campaigns',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/donations',
    element: CanonicalDonationsRedirect,
    title: 'Donations',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/transparency',
    element: CanonicalTransparencyRedirect,
    title: 'Your impact',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/sponsorship/mine',
    element: CanonicalMySponsorshipsRedirect,
    title: 'My sponsorships',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/updates/mine',
    element: CanonicalMyUpdatesRedirect,
    title: 'Updates',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/receipts/mine',
    element: CanonicalMyReceiptsRedirect,
    title: 'Receipts',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants',
    element: CanonicalGrantsRedirect,
    title: 'Grants',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/funders',
    element: CanonicalGrantsFundersRedirect,
    title: 'Funders',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/applications',
    element: CanonicalGrantsApplicationsRedirect,
    title: 'Applications',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/opportunities',
    element: CanonicalGrantsOpportunitiesRedirect,
    title: 'Opportunities',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/snippets',
    element: CanonicalGrantsSnippetsRedirect,
    title: 'Snippets',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/discover',
    element: CanonicalGrantsDiscoverRedirect,
    title: 'Discover grants',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/reporting',
    element: CanonicalGrantsReportingRedirect,
    title: 'Reporting calendar',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/compliance',
    element: CanonicalGrantsComplianceRedirect,
    title: 'Compliance & statements',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/grants/directory',
    element: CanonicalGrantsDirectoryRedirect,
    title: 'Funder directory',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/messages',
    element: CanonicalMessagesRedirect,
    title: 'Messages',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/ai/dashboard',
    element: CanonicalAiDashboardRedirect,
    title: 'AI Dashboard',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/ai/v2',
    element: CanonicalAiV2Redirect,
    title: 'Command Center V2',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/ai/agents',
    element: CanonicalAiAgentsRedirect,
    title: 'AI Agents',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/ai/actions',
    element: AIActionsRedirect,
    title: 'AI Findings',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/budget',
    element: CanonicalBudgetDashboardRedirect,
    title: 'Budget',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/fundraising/events',
    element: CanonicalFundraisingEventsRedirect,
    title: 'Fundraising Events',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/transactions/income',
    element: CanonicalTransactionsIncomeRedirect,
    title: 'Income',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/transactions/expenses',
    element: CanonicalTransactionsExpensesRedirect,
    title: 'Expenses',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/transactions',
    element: CanonicalTransactionsAllRedirect,
    title: 'Transactions',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/finance/reports',
    element: CanonicalFinanceReportsRedirect,
    title: 'Financial Reports',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/teams/documents',
    element: CanonicalTeamsDocumentsRedirect,
    title: 'Team Documents',
    requiresAuth: true
  },
  // ── Unified sign-off inbox (canonical) ──────────────────────────
  // Workspace-admin review surface: every AI artifact awaiting human
  // sign-off. Gated to the `admin` section (owner/admin visible_sections);
  // the backend enforces membership regardless.
  {
    path: '/w/:workspaceId/sign-off',
    element: withSection('admin', SignOffInboxPage),
    title: 'Pending sign-off',
    requiresAuth: true
  },
  // ── Workspace ▸ Integrations ────────────────────────────────────
  // Third-party connections (AI provider keys today; accounting and
  // spreadsheets later). Gated to the `admin` section — connecting an
  // integration hands a third party a foothold in the workspace's data, so
  // it is an ownership decision. The backend independently requires
  // owner/admin AND feature.integrations.
  {
    path: '/w/:workspaceId/integrations',
    element: withSection('admin', IntegrationsPage),
    title: 'Integrations',
    requiresAuth: true
  },
  {
    path: '/sign-off/:seed',
    element: SignOffLegacyRedirect,
    title: 'Pending sign-off',
    requiresAuth: true
  },
  // ── Writing surface (canonical) ─────────────────────────────────
  // Documents moved to Workspace → Writing → Library; the legacy
  // /teams/documents/* routes still render AiDocumentsPage for back-
  // compat, but new links point to /w/:workspaceId/writing/library.
  {
    path: '/w/:workspaceId/writing/library',
    element: WritingLibraryPage,
    title: 'Writing — Library',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/drafts',
    element: DraftsListPage,
    title: 'Writing — Drafts',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/newsletters',
    element: NewslettersListPage,
    title: 'Writing — Newsletters',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/blogs',
    element: BlogsListPage,
    title: 'Writing — Blogs',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/subscribers',
    element: SubscribersPage,
    title: 'Writing — Subscribers',
    requiresAuth: true
  },
  // Legal / governance document vault (Content → Documents). Gated in the
  // sidebar on feature.legal_documents; the page's write actions gate on
  // manage_legal_documents (read on view_legal_documents) at the API.
  {
    path: '/w/:workspaceId/legal/documents',
    element: LegalDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  {
    path: '/legal/documents',
    element: LegalDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/templates',
    element: WritingTemplatesRedirect,
    title: 'Templates',
    requiresAuth: true
  },
  {
    // Compose is a modal on the drafts list now, not its own page. Old
    // bookmarks/deep-links redirect there with ?compose=1 (+ kind /
    // template_id carried over) and the list auto-opens the modal.
    path: '/w/:workspaceId/writing/compose',
    element: ComposeRedirect,
    title: 'Writing — Compose',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/draft/:draftId',
    element: DraftEditorPage,
    title: 'Writing — Draft',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/newsletter/:id',
    element: NewsletterEditorPage,
    title: 'Writing — Newsletter',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/writing/blog/:id',
    element: BlogEditorPage,
    title: 'Writing — Blog',
    requiresAuth: true
  },
  // Parent /w/:workspaceId/writing redirects to Library (default child).
  {
    path: '/w/:workspaceId/writing',
    element: (() => {
      const Redir = () => {
        const { workspaceId } = useParams();
        return <Navigate to={`/w/${workspaceId}/writing/library`} replace />;
      };
      return Redir;
    })(),
    title: 'Writing',
    requiresAuth: true
  },
  // Public, unauthenticated landing pages for the compliance loop.
  // Subscribers click links inside emails to land here — no workspace
  // context, no auth. `/u/:token` is the destination of every
  // newsletter footer + `List-Unsubscribe` header. `/c/:token` is the
  // confirmation link sent by the double-opt-in flow.
  {
    path: '/u/:token',
    element: UnsubscribeLandingPage,
    title: 'Unsubscribe',
    requiresAuth: false
  },
  {
    path: '/c/:token',
    element: ConfirmSubscriptionPage,
    title: 'Confirm subscription',
    requiresAuth: false
  },
  {
    path: '/w/:workspaceId/ai/documents',
    element: CanonicalAiDocumentsRedirect,
    title: 'AI Documents',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/feed',
    element: CanonicalFeedRedirect,
    title: 'Feed',
    requiresAuth: true
  },
  {
    path: '/w/:workspaceId/contacts',
    element: CanonicalContactsRedirect,
    title: 'Contacts',
    requiresAuth: true
  },
  // ── Existing routes ──────────────────────────────────────────────
  {
    path: '/',
    element: () => <RedirectPreservingQuery to="/identity/login" />,
    title: 'Home'
  },
  { path: '/profile', element: ProfileRedirect, title: 'My Profile' },
  { path: '/dashboard', element: DashboardRedirect, title: 'Dashboard' },
  { path: '/messages', element: MessagesRedirect, title: 'Messages' },
  { path: '/sponsorship', element: SponsorshipRedirect, title: 'Sponsorship' },
  { path: '/campaigns', element: CampaignsRedirect, title: 'Campaigns' },
  { path: '/donations', element: DonationsRedirect, title: 'Donations' },
  { path: '/grants', element: GrantsRedirect, title: 'Grants' },
  {
    path: '/fundraising/events',
    element: FundraisingEventsRedirect,
    title: 'Fundraising Events'
  },
  {
    path: '/fundraising/forms',
    element: DonationFormsRedirect,
    title: 'Donation Forms'
  },
  {
    path: '/fundraising/relief',
    element: ReliefRedirect,
    title: 'Relief Appeals'
  },
  { path: '*', element: NotFound, title: 'Not Found' },
  { path: '/welcome', element: Welcome, title: 'Welcome' },
  { path: '/contact', element: Contact, title: 'Contact' },
  { path: '/search', element: SearchResults, title: 'Search' },
  { path: '/home', element: HomeRedirect, title: 'Home' },
  { path: '/home/:seed', element: Home, title: 'Home' },
  { path: '/about', element: About, title: 'About' },
  { path: '/sunrise', element: Sunrise, title: 'Sunrise' },
  { path: '/marketplace', element: MarketPlacePage, title: 'Marketplace' },
  {
    path: '/marketplace/shop',
    element: ShopDetailsPage,
    title: 'Shop Details'
  },
  {
    path: '/marketplace/upload',
    element: UploadProductPage,
    title: 'Upload Product'
  },
  {
    path: '/sprout/:user',
    element: Sprout,
    title: 'AI Assistant',
    requiresAuth: true
  },
  {
    path: '/teams/:team_id/:seed_id',
    element: TeamDashboard,
    title: 'teams',
    requiresAuth: true
  },
  // ── Theme (NFTMax) reference pages — DEV ONLY ──────────────────────
  // Orphaned NFTMax theme pages reused as design sources, grouped under
  // Workspace ▸ Theme in the sidebar. Gated on NODE_ENV so they are compiled
  // into the dev bundle (`yarn start`) but NEVER into the production build
  // (`yarn build`) that ships to EC2/CloudFront. Workspace resolved from :seed.
  ...(process.env.NODE_ENV === 'development'
    ? [
        {
          path: '/theme/active-bids/:seed',
          element: ActiveBids,
          title: 'Active Bids',
          requiresAuth: true
        },
        {
          path: '/theme/marketplace/:seed',
          element: ThemeMarketPlace,
          title: 'Marketplace',
          requiresAuth: true
        },
        {
          path: '/theme/my-collection/:seed',
          element: ThemeMyCollection,
          title: 'My Collection',
          requiresAuth: true
        },
        {
          path: '/theme/saved/:seed',
          element: ThemeSaved,
          title: 'Saved',
          requiresAuth: true
        },
        {
          path: '/theme/sell/:seed',
          element: ThemeSell,
          title: 'Sell',
          requiresAuth: true
        },
        {
          path: '/theme/thank-you/:seed',
          element: ThemeThankYou,
          title: 'Thank You',
          requiresAuth: true
        },
        // MyWallet is an NFTMax template page exactly like its siblings above:
        // it renders hardcoded crypto wallets (MetaMask / Coinbase Wallet /
        // Bitski / WalletConnect, $734.79 each) plus ETH/BTC/LTC balance rings
        // and a "Buy Nft art from LTC" transaction feed -- none of it
        // API-backed. It was registered outside this fence and so shipped to
        // production, putting invented financial figures on a reachable route
        // of a charity-transparency platform. Fenced here with the rest of the
        // theme references. Path stays /my-wallet (no :seed segment -- the page
        // resolves no workspace) and nothing in the app links to it.
        {
          path: '/my-wallet',
          element: MyWalletPage,
          title: 'Wallet',
          requiresAuth: true
        }
      ]
    : []),
  {
    path: '/teams/:team_id/events/:seed_id',
    element: TeamEvents,
    title: 'Team Events',
    requiresAuth: true
  },
  {
    path: '/fundraising/events/:seed_id',
    element: TeamEvents,
    title: 'Fundraising Events',
    requiresAuth: true
  },
  {
    path: '/fundraising/forms/:seed_id',
    element: DonationFormsListPage,
    title: 'Donation Forms',
    requiresAuth: true
  },
  {
    path: '/fundraising/relief/:seed_id',
    element: ReliefAppealsListPage,
    title: 'Relief Appeals',
    requiresAuth: true
  },
  {
    path: '/assignments/:team_id/:seed_id',
    element: Assignments,
    title: 'Assignments',
    requiresAuth: true
  },
  {
    // Retired — redirects to /contacts (legacy page superseded by the Contacts
    // page + Settings ▸ Members & Access).
    path: '/teams/directories',
    element: DirectoriesRetiredRedirect,
    title: 'Contacts',
    requiresAuth: true
  },
  {
    // Contacts — external CRM as its own top-level destination (nav rework).
    // Workspace resolved from ?seedId, mirroring /teams/directories.
    path: '/contacts',
    element: withSection('contacts', ContactsPage),
    title: 'Contacts',
    requiresAuth: true
  },
  {
    path: '/sponsor/projects/:seedId',
    element: SponsorProjectsPage,
    title: 'Projects',
    requiresAuth: true
  },
  {
    // Workspace-wide Projects — the "Projects" destination in the Work section.
    // Canonical `/w/:workspaceId/...` shape so `useActiveWorkspace()` reads the
    // id from route params. Gated on the `work` section (defense in depth).
    path: '/w/:workspaceId/projects',
    element: withSection('work', WorkspaceProjectsPage),
    title: 'Projects',
    requiresAuth: true
  },
  {
    // Teams index — the "Teams" destination in the Work section (replaces the
    // inline sidebar team list). Gated on the `work` section.
    path: '/w/:workspaceId/teams',
    element: withSection('work', TeamsIndexPage),
    title: 'Teams',
    requiresAuth: true
  },
  {
    // My Work — the flagship "Work" destination: tasks assigned to me across
    // every team in the workspace. Gated on the `work` section.
    path: '/w/:workspaceId/my-work',
    element: withSection('work', MyWorkPage),
    title: 'My Work',
    requiresAuth: true
  },
  {
    // My spending — the private (personal workspace) persona's spending
    // dashboard. Linked from the Finance section only for personal
    // workspaces; gated on `finance` like its sibling money surfaces.
    path: '/w/:workspaceId/my-spending',
    element: withSection('finance', MySpendingPage),
    title: 'My spending',
    requiresAuth: true
  },
  {
    path: '/sponsor/campaigns/:seedId',
    element: SponsorCampaignsPage,
    title: 'Campaigns',
    requiresAuth: true
  },
  {
    path: '/sponsor/events/:seedId',
    element: SponsorEventsPage,
    title: 'Events',
    requiresAuth: true
  },
  {
    path: '/sponsor/recipients/:seedId',
    element: SponsorRecipientsPage,
    title: 'Recipients',
    requiresAuth: true
  },
  {
    // Sponsor transparency report — "where my money went". Gated on the
    // transparency section so a non-sponsor who deep-links here hits the
    // Restricted view (defense in depth; the endpoint is self-scoped anyway).
    path: '/transparency/:seedId',
    element: withSection('transparency', SponsorTransparencyPage),
    title: 'Your impact',
    requiresAuth: true
  },
  {
    // Sponsor self-service: the sponsor's own recurring sponsorships in this
    // workspace, with pause/resume/cancel. Read-mostly; gated on the
    // transparency section (sponsors/auditors), defense-in-depth.
    path: '/sponsorship/mine/:seed',
    element: withSection('transparency', SponsorMySponsorshipsPage),
    title: 'My sponsorships',
    requiresAuth: true
  },
  {
    // Sponsor self-service: the sponsor's own receipts / tax docs in this
    // workspace (download). Self-scoped server-side by payer_email; gated on
    // the transparency section (sponsors/auditors), defense-in-depth.
    path: '/receipts/mine/:seed',
    element: withSection('transparency', SponsorReceiptsPage),
    title: 'Receipts',
    requiresAuth: true
  },
  {
    // Sponsor full activity feed — every gift/expense/update behind the
    // dashboard's "Updates, right now" card ("View all" routes here).
    // Self-scoped server-side by sponsor email; transparency-gated.
    path: '/updates/mine/:seed',
    element: withSection('transparency', SponsorUpdatesPage),
    title: 'Updates',
    requiresAuth: true
  },
  {
    path: '/teams/directories/workflows',
    element: DirectoriesWorkflow,
    title: 'Workflow Builder',
    requiresAuth: true
  },
  {
    path: '/settings/automations',
    element: DirectoriesWorkflow,
    title: 'Automations',
    requiresAuth: true
  },
  {
    // Gated, not bare: the sidebar already stops offering workspace Settings
    // to anyone whose role ceiling lacks `settings`, but a hidden menu entry
    // restricts nothing — `/settings?scope=seed` typed in rendered the whole
    // Workspace Settings tab for a contributor. The gate applies to the
    // WORKSPACE scope only; personal settings stay open to every viewer.
    path: '/settings',
    element: withSettingsScope(Settings),
    title: 'Settings',
    requiresAuth: true
  },
  {
    path: '/help',
    element: HelpPage,
    title: 'Help',
    requiresAuth: true
  },
  {
    path: '/projects/:project_id/:seed_id',
    element: Projects,
    title: 'projects',
    requiresAuth: true
  },
  { path: '/shop', element: MarketPlacePage, title: 'Shop' },
  { path: '/shop/cart/', element: Cart, title: 'Cart' },
  { path: '/shop/checkout', element: ShopCheckoutPage, title: 'Checkout' },
  {
    path: '/shop/checkout/success',
    element: ShopCheckoutSuccessPage,
    title: 'Order Confirmed'
  },
  { path: '/shop/store/:id', element: Store, title: 'Store' },
  { path: '/shop/Filter', element: Filter, title: 'Filter' },
  { path: '/shop/product', element: ProductsCatalogPage, title: 'Product' },
  { path: '/shop/product/list/:id', element: ProductListPage, title: 'List' },
  {
    path: '/shop/product/images/',
    element: ProductImagesPage,
    title: 'Preview Images'
  },
  {
    path: '/shop/product/detail/:productId',
    element: ProductDetailPage,
    title: 'Detail'
  },
  {
    path: '/shop/product/Detail/',
    element: ProductDetailPage,
    title: 'Detail'
  },
  { path: '/seed/:id', element: SeedProfile, title: 'Seed Profile' },
  {
    path: '/seed/:id/operation',
    element: SeedOperation,
    title: 'Seed Operation'
  },
  { path: '/pod/:country', element: PodProfile, title: 'Pod Profile' },
  {
    path: '/plant',
    element: PlantSeed,
    title: 'Plant Seed',
    requiresAuth: true
  },
  {
    path: '/onboard',
    element: Onboard,
    title: 'Onboard Seed',
    requiresAuth: true
  },
  {
    path: '/onboard/:userId',
    element: Onboard,
    title: 'Onboard Seed',
    requiresAuth: true
  },
  {
    path: '/dashboard/:id',
    element: Dashboard,
    title: 'Dasboard',
    requiresAuth: true
  },
  {
    path: '/user-profile',
    element: UserProfilePage,
    title: 'User Profile',
    requiresAuth: true
  },
  {
    path: '/profile/:pk',
    element: AuthProfilePage,
    title: 'User Auth Profile'
  },
  {
    path: '/profile/organization/:seedId',
    element: OrganizationProfilePage,
    title: 'Organization Profile'
  },
  { path: '/user/:pk', element: UserProfile, title: 'User Profile' },
  {
    path: '/messages/:seed',
    element: Conversations,
    title: 'User Inbox',
    requiresAuth: true
  },
  {
    path: '/ai/dashboard/:seed',
    element: AiDashboardPage,
    title: 'AI Dashboard',
    requiresAuth: true
  },
  {
    path: '/ai/dashboard',
    element: AiDashboardPage,
    title: 'AI Dashboard',
    requiresAuth: true
  },
  {
    path: '/ai/v2/:seed',
    element: CommandCenterV2Page,
    title: 'Command Center V2',
    requiresAuth: true
  },
  {
    path: '/ai/v2',
    element: CommandCenterV2Page,
    title: 'Command Center V2',
    requiresAuth: true
  },
  {
    path: '/ai/analytics/:seed',
    element: AIQualityAnalyticsPage,
    title: 'AI Analytics',
    requiresAuth: true
  },
  {
    path: '/ai/analytics',
    element: AIQualityAnalyticsPage,
    title: 'AI Analytics',
    requiresAuth: true
  },
  {
    path: '/ai/agents/:seed',
    element: AiAgentsPage,
    title: 'AI Agents',
    requiresAuth: true
  },
  {
    path: '/ai/agents',
    element: AiAgentsPage,
    title: 'AI Agents',
    requiresAuth: true
  },
  {
    path: '/ai/actions/:seed',
    element: AIActionsRedirect,
    title: 'AI Findings',
    requiresAuth: true
  },
  {
    path: '/ai/actions',
    element: AIActionsRedirect,
    title: 'AI Findings',
    requiresAuth: true
  },
  // Documents live under Teams in the sidebar — each team can upload and
  // browse its own files. The `/ai/documents/*` paths are kept as aliases
  // for backward compatibility with older bookmarks + deep links.
  {
    path: '/teams/documents/:seed',
    element: AiDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  {
    path: '/teams/documents',
    element: AiDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  {
    path: '/ai/documents/:seed',
    element: AiDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  {
    path: '/ai/documents',
    element: AiDocumentsPage,
    title: 'Documents',
    requiresAuth: true
  },
  // Workspace / teamspace feed — follow-filtered broadcast thread.
  {
    path: '/workspaces/:seed/feed',
    element: WorkspaceFeedPage,
    title: 'Feed',
    requiresAuth: true
  },
  {
    path: '/teams/:seed/feed',
    element: WorkspaceFeedPage,
    title: 'Feed',
    requiresAuth: true
  },
  // ── Identity routes ──
  { path: '/identity/reset', element: Reset, title: 'Reset' },
  {
    path: '/identity/reset-confirm/:email',
    element: ResetConfirm,
    title: 'Reset Confirm'
  },
  { path: '/identity/register', element: Register, title: 'Register' },
  { path: '/identity/login', element: Login, title: 'Login' },
  { path: '/invite/accept', element: InviteAcceptPage, title: 'Accept invite' },
  { path: '/join/:workspaceId', element: JoinPage, title: 'Join & Support' },
  {
    // Public donor-facing donation form (standalone forms). First-pass
    // design surface — resolves a form by slug from mock data. Anonymous,
    // no auth (mirrors the other /donate public surfaces below).
    path: '/forms/:workspaceId/:slug',
    element: PublicDonationFormPage,
    title: 'Donate',
    requiresAuth: false
  },
  {
    // Public relief-appeal page (Verified Relief Appeals). Anonymous —
    // renders from the one public aggregate; its donate CTA lands on the
    // public donation-form route above (existing checkout, zero new money
    // code). The backend's workspace-scoped flag rule decides availability;
    // the page renders its "unavailable" state on 403/404.
    path: '/appeals/:workspaceId/:slug',
    element: PublicReliefAppealPage,
    title: 'Relief Appeal',
    requiresAuth: false
  },
  // Public donate flow — anonymous-friendly. Routed through the
  // PublicEntityRegistry so adding new shareable entities (recipient
  // sponsorship, future marketplace product, etc.) is a one-line
  // descriptor change. Each entry stays `requiresAuth: false` so the
  // route guard never intercepts a logged-out visitor on a shared
  // link.
  // NOTE: no '/donate/workspace' or '/donate/event' route. Those public
  // surfaces were stray duplicates and were removed (development dropped
  // workspace; this branch dropped event). The canonical donor entry is
  // '/join/:workspaceId' (+ ?context=event&target=<eventId> for an event).
  {
    path: '/donate/campaign/:campaignId',
    element: PublicCampaignDonatePage,
    title: 'Support campaign',
    requiresAuth: false
  },
  {
    path: '/donate/recipients/:workspaceId',
    element: PublicWorkspaceRecipientsPage,
    title: 'Sponsor a recipient',
    requiresAuth: false
  },
  // Marketplace public buy. Behind `feature.marketplace` on the
  // backend (scope-freeze flag). The route is wired today so the
  // URL shape is locked in and ready when the flag opens for a
  // paying customer; until then, the backend public endpoint
  // returns 401/403 and PublicEntityPage surfaces an error state.
  // requiresAuth: false because the intent is anonymous-friendly
  // public buy, same as the donate routes.
  {
    path: '/buy/product/:productId',
    element: PublicProductBuyPage,
    title: 'Buy',
    requiresAuth: false
  },
  // Phase 5 — donor-public transparency surface for grants. Anonymous,
  // no auth, no section gate. Backend enforces is_public=True; a
  // not-published grant 404s upstream and the page renders an
  // explanatory empty state.
  // Public calendar booking links. Anonymous: a workspace publishes a
  // tokenised page and whoever holds the link picks an open slot. The token in
  // the path IS the authorisation, and there is deliberately no workspace id —
  // the holder of a printed link has no workspace context, and a caller that
  // could name a workspace could aim a booking at one whose link they were
  // never given.
  //
  // `requiresAuth: false` on both, and it matters more here than on the donate
  // routes: the backend clears `authentication_classes` on these endpoints so
  // a visitor carrying a stale JWT from some other session is served as if
  // they carried nothing. A route guard that intercepted them would undo that.
  //
  // The manage route is nested under `/booking/manage/` so a manage token can
  // never be routed to the page endpoint, or vice versa, by a client that
  // mixed the two up — they are different credentials with different reach.
  {
    path: '/booking/manage/:manageToken',
    element: BookingManagePage,
    title: 'Your appointment',
    requiresAuth: false
  },
  {
    path: '/booking/:token',
    element: PublicBookingPage,
    title: 'Book an appointment',
    requiresAuth: false
  },
  {
    path: '/public/grants/:workspaceId',
    element: PublicWorkspaceGrantsPage,
    title: 'Public grants',
    requiresAuth: false
  },
  {
    path: '/public/grants/:workspaceId/:grantId',
    element: PublicGrantPage,
    title: 'Grant transparency record',
    requiresAuth: false
  },
  // Authenticated donor's own gift history. Backing endpoint joins
  // by email at read time, so anonymous-but-emailed past gifts surface
  // here as soon as the donor signs up with the matching email.
  {
    path: '/donations/mine',
    element: MyDonationsPage,
    title: 'Your donations',
    requiresAuth: true
  },
  // Passwordless magic-link sign-in. Request page collects the
  // email and posts to /identity/magic-link/request/; verify page
  // reads ?token=... from the email URL and posts to
  // /identity/magic-link/verify/. Both routes MUST be `requiresAuth:
  // false` — they're the path for anonymous visitors becoming
  // authed users in the first place.
  {
    path: '/identity/magic-link/request',
    element: MagicLinkRequestPage,
    title: 'Sign in by email',
    requiresAuth: false
  },
  {
    path: '/identity/magic-link/verify',
    element: MagicLinkVerifyPage,
    title: 'Signing in',
    requiresAuth: false
  },
  {
    path: '/identity/confirm-email',
    element: ConfirmEmail,
    title: 'Confirm Email'
  },
  {
    path: '/identity/email-confirmed/:token',
    element: EmailConfirmed,
    title: 'Email Confirmed'
  },
  {
    path: '/identity/email-confirmed',
    element: EmailConfirmed,
    title: 'Email Confirmed'
  },
  {
    path: '/identity/invite/accept',
    element: AcceptInvite,
    title: 'Accept Invite'
  },
  {
    path: '/identity/password-reset-confirm/:uidb64/:token',
    element: PasswordResetConfirm,
    title: 'Password Reset Confirm'
  },
  {
    path: '/identity/reset-password-success',
    element: ResetPasswordSuccess,
    title: 'Reset Password Success'
  },
  {
    path: '/identity/registration-error',
    element: RegistrationError,
    title: 'Registration Error'
  },
  // ── Legacy redirects (keep old paths working) ──
  {
    path: '/login',
    element: () => <RedirectPreservingQuery to="/identity/login" />,
    title: 'Login Redirect'
  },
  {
    path: '/register',
    element: () => <Navigate to="/identity/register" replace />,
    title: 'Register Redirect'
  },
  {
    path: '/reset',
    element: () => <Navigate to="/identity/reset" replace />,
    title: 'Reset Redirect'
  },
  {
    path: '/confirmEmail',
    element: () => <Navigate to="/identity/confirm-email" replace />,
    title: 'Confirm Email Redirect'
  },
  {
    path: '/EmailConfirmed/:token',
    element: () => <Navigate to="/identity/email-confirmed" replace />,
    title: 'Email Confirmed Redirect'
  },
  {
    path: '/EmailConfirmed',
    element: () => <Navigate to="/identity/email-confirmed" replace />,
    title: 'Email Confirmed Redirect'
  },
  {
    path: '/invite/accept',
    element: () => <Navigate to="/identity/invite/accept" replace />,
    title: 'Accept Invite Redirect'
  },
  {
    path: '/PasswordResetConfirm/:uidb64/:token',
    element: PasswordResetLegacyRedirect,
    title: 'Password Reset Redirect'
  },
  {
    path: '/resetconfirm/:email',
    element: () => <Navigate to="/identity/reset-confirm" replace />,
    title: 'Reset Confirm Redirect'
  },
  {
    path: '/resetpasswordsuccess',
    element: () => <Navigate to="/identity/reset-password-success" replace />,
    title: 'Reset Success Redirect'
  },
  {
    path: '/RegistrationError',
    element: () => <Navigate to="/identity/registration-error" replace />,
    title: 'Registration Error Redirect'
  },
  {
    path: '/share/accept/:token',
    element: ShareAcceptPage,
    title: 'Accept shared link'
  },
  {
    path: '/share/invitation/:token',
    element: ShareInvitationAcceptPage,
    title: 'Accept invitation'
  },
  {
    path: '/share/budget/:budgetId',
    element: ShareBudgetViewerPage,
    title: 'Shared budget'
  },
  {
    path: '/share/report/:reportId',
    element: ShareReportViewerPage,
    title: 'Shared report'
  },
  {
    path: '/w/:workspaceId/sharing/active-shares',
    element: WorkspaceSharesPage,
    title: 'Active shares',
    requiresAuth: true
  },
  {
    path: '/budget/dashboard/:seed',
    element: withSection('finance', BudgetDashboard),
    title: 'Budget Dashboard',
    requiresAuth: true
  },
  {
    path: '/budget/dashboard-v2/:seed',
    element: withSection('finance', BudgetDashboardV2),
    title: 'Budget Dashboard V2',
    requiresAuth: true
  },
  {
    path: '/budget/templates/:seed',
    element: BudgetTemplatesRedirect,
    title: 'Budget Templates',
    requiresAuth: true
  },
  {
    path: '/budget/bank/:seed',
    element: withSection('finance', BankSectionPage),
    title: 'Bank',
    requiresAuth: true
  },
  // Legacy routes redirect to the canonical shell.
  {
    path: '/budget/bank-connections/:seed',
    element: BankConnectionsLegacyRedirect,
    title: 'Bank Connections',
    requiresAuth: true
  },
  {
    path: '/budget/bank-feed-inbox/:seed',
    element: BankFeedInboxLegacyRedirect,
    title: 'Bank Feed Inbox',
    requiresAuth: true
  },
  {
    path: '/upload',
    element: FileUploader,
    title: 'File Uploader',
    requiresAuth: true
  },
  {
    path: '/subscriptions/pricing/twotier',
    element: TwoTierPricing,
    title: 'Two Tier Pricing'
  },
  {
    path: '/sponsorship/:seed',
    element: Sponsorship,
    title: 'Sponsorship',
    requiresAuth: true
  },
  {
    // The update-due queue: which recipients' sponsors have waited longest
    // for news. Lives beside the sponsorship surface it feeds — its rows
    // deep-link into /sponsorship/:seed?child=<id> (the update composer).
    path: '/sponsorship/:seed/updates-due',
    element: UpdatesDuePage,
    title: 'Updates due',
    requiresAuth: true
  },
  {
    path: '/campaigns/:seed',
    element: CampaignsPage,
    title: 'Campaigns',
    requiresAuth: true
  },
  {
    path: '/campaigns/:seed/emails',
    element: CampaignsPage,
    title: 'Email campaigns',
    requiresAuth: true
  },
  {
    path: '/campaigns/:seed/newsletters',
    element: NewsletterCampaigns,
    title: 'Newsletter campaigns',
    requiresAuth: true
  },
  {
    path: '/campaigns/:seed/social',
    element: SocialMediaCampaigns,
    title: 'Social media campaigns',
    requiresAuth: true
  },
  {
    path: '/campaigns/newsletters',
    element: NewsletterCampaigns,
    title: 'Newsletter campaigns',
    requiresAuth: true
  },
  {
    path: '/campaigns/social',
    element: SocialMediaCampaigns,
    title: 'Social media campaigns',
    requiresAuth: true
  },
  { path: '/sponsor', element: SponsorChild, title: 'Sponsor' },
  {
    path: '/donations/:seed',
    element: Donations,
    title: 'Donations',
    requiresAuth: true
  },
  {
    // Payroll sits under the `finance` section gate: a pay statement is a
    // money document, and reusing the existing section keeps the sidebar
    // entry and the destination page gated by the same visible_sections key.
    path: '/payroll/:seed',
    element: withSection('finance', PayStatementsPage),
    title: 'Pay statements',
    requiresAuth: true
  },
  {
    // Scheduling sits under the `work` section gate — it is programme
    // execution, not fundraising or finance, and `work` is the section its
    // sidebar entry lives in. The two MUST match: `sectionVisibility.ts`
    // states the route gate and the sidebar section share a key.
    path: '/scheduling/:seed',
    element: withSection('work', SchedulingPage),
    title: 'Scheduling',
    requiresAuth: true
  },
  {
    path: '/grants/:seed',
    element: withSection('giving', GrantsPipelinePage),
    title: 'Grants',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/funders',
    element: withSection('giving', FunderCatalogPage),
    title: 'Funders',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/applications',
    element: withSection('giving', GrantApplicationsPage),
    title: 'Applications',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/snippets',
    element: withSection('giving', SnippetLibraryPage),
    title: 'Snippets',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/opportunities',
    element: withSection('giving', OpportunitiesPage),
    title: 'Opportunities',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/discover',
    element: GrantsDiscoverRedirect,
    title: 'Discover grants',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/matches',
    element: withSection('giving', GrantMatchesPage),
    title: 'Grant matches',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/reporting',
    element: withSection('giving', ReportingCalendarPage),
    title: 'Reporting calendar',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/compliance',
    element: withSection('giving', ComplianceStatementsPage),
    title: 'Compliance & statements',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/directory',
    element: withSection('giving', FunderDirectoryPage),
    title: 'Funder directory',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/:grantId',
    element: withSection('giving', GrantDetailPage),
    title: 'Grant detail',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/:grantId/application',
    element: withSection('giving', GrantApplicationEditor),
    title: 'Grant application',
    requiresAuth: true
  },
  {
    path: '/grants/:seed/:grantId/reports/:reportId',
    element: withSection('giving', ReportEditor),
    title: 'Grant report',
    requiresAuth: true
  },
  {
    path: '/transactions/income/:seed_id',
    element: withSection('finance', Income),
    title: 'Income',
    requiresAuth: true
  },
  {
    path: '/transactions/expenses/:seed_id',
    element: withSection('finance', Expenses),
    title: 'Expenses',
    requiresAuth: true
  },
  {
    path: '/transactions/all/:seed_id',
    element: withSection('finance', Transactions),
    title: 'Transactions',
    requiresAuth: true
  },
  {
    path: '/recycle-bin/:seed',
    element: withSection('admin', RecycleBinPage),
    title: 'Recycle bin',
    requiresAuth: true
  },
  {
    path: '/templates/:seed',
    element: withSection('admin', AllTemplatesPage),
    title: 'All templates',
    requiresAuth: true
  },
  {
    path: '/templates',
    element: withSection('admin', AllTemplatesPage),
    title: 'All templates',
    requiresAuth: true
  },
  {
    path: '/categories/:seed',
    element: withSection('finance', CategoriesPage),
    title: 'Categories',
    requiresAuth: true
  },
  {
    path: '/finance/reports/:seed_id',
    element: withSection('finance', FinancialReports),
    title: 'Financial Reports',
    requiresAuth: true
  },
  {
    path: '/finance/reports',
    element: withSection('finance', FinancialReports),
    title: 'Financial Reports',
    requiresAuth: true
  },
  {
    // Auditor "Audit trail" — workspace-wide read-only change history.
    // Gated on `finance` (the auditor persona carries that visible_section;
    // audit is a finance-governance surface), so no new GateSection needed.
    path: '/audit-trail/:seed',
    element: withSection('finance', AuditTrailPage),
    title: 'Audit trail',
    requiresAuth: true
  },
  {
    // Auditor sample-testing (P3): largest transactions + evidence drill-down.
    // Gated on `finance` like the audit trail (the auditor persona carries it).
    path: '/audit-sample/:seed',
    element: withSection('finance', AuditSampleTestingPage),
    title: 'Sample testing',
    requiresAuth: true
  },
  {
    path: '/notification/:seed',
    element: NotificationsPage,
    title: 'Notification',
    requiresAuth: true
  },
  {
    path: '/contributor-onboard',
    element: ContributorOnboard,
    title: 'Contributor Onboard',
    requiresAuth: true
  }
];

export default routes;
